import {
  AlertTriangle,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  Edit2,
  ExternalLink,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import * as XLSX from 'xlsx'
import { activatableRowProps } from '@/components/admin/shared/activatable-row'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { StudyPlanFormModal } from '@/components/admin/shared/study-plan-form-modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  REVEAL_ON_HOVER,
  REVEAL_ON_HOVER_DECORATIVE,
} from '@/components/ui/reveal-on-hover'
import { getDifficultyColorClass, getDifficultyLabel } from '@/lib/difficulty'
import { cn } from '@/lib/utils'
import { useCoachingQuestions } from '../problems/hooks/use-questions'
import type { StudyPlan, StudyPlanFormData } from './hooks/use-study-plans'
import { groupStudyPlansBySource, useStudyPlans } from './hooks/use-study-plans'

interface StudyPlanDetailModalProps {
  studyPlan: StudyPlan
  onClose: () => void
  onAddProblems: (problemIds: string[]) => Promise<void>
  onAddAndLinkSource?: (problemIds: string[]) => Promise<void>
  onRemoveProblem: (problemId: string) => Promise<void>
  sourceId?: string
  defaultIcon?: string | null
  defaultUrl?: string | null
}

function StudyPlanDetailModal({
  studyPlan,
  onClose,
  onAddProblems,
  onAddAndLinkSource,
  onRemoveProblem,
  sourceId,
  defaultIcon,
  defaultUrl,
}: StudyPlanDetailModalProps) {
  const { questions, loading: questionsLoading } = useCoachingQuestions()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProblemIds, setSelectedProblemIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [isAdding, setIsAdding] = useState(false)
  const [showAddSection, setShowAddSection] = useState(false)

  const existingProblemIds = useMemo(
    () => new Set(studyPlan.problems.map((p) => p.id)),
    [studyPlan.problems],
  )

  const filteredQuestions = useMemo(() => {
    if (!searchQuery.trim()) return []
    const query = searchQuery.toLowerCase()
    return questions
      .filter(
        (q) =>
          !existingProblemIds.has(q.id) &&
          (q.name.toLowerCase().includes(query) ||
            q.externalId?.toLowerCase().includes(query)),
      )
      .slice(0, 20)
  }, [questions, searchQuery, existingProblemIds])

  const handleToggleProblem = (problemId: string) => {
    setSelectedProblemIds((prev) => {
      const next = new Set(prev)
      if (next.has(problemId)) {
        next.delete(problemId)
      } else {
        next.add(problemId)
      }
      return next
    })
  }

  const handleAddSelected = async () => {
    if (selectedProblemIds.size === 0) return
    setIsAdding(true)
    try {
      await onAddProblems(Array.from(selectedProblemIds))
      setSelectedProblemIds(new Set())
      setSearchQuery('')
      setShowAddSection(false)
      toast.success(`Added ${selectedProblemIds.size} problem(s)`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add problems')
    } finally {
      setIsAdding(false)
    }
  }

  const handleAddAndLinkSource = async () => {
    if (selectedProblemIds.size === 0 || !onAddAndLinkSource) return
    setIsAdding(true)
    try {
      await onAddAndLinkSource(Array.from(selectedProblemIds))
      setSelectedProblemIds(new Set())
      setSearchQuery('')
      setShowAddSection(false)
      toast.success(
        `Added ${selectedProblemIds.size} problem(s) and linked source`,
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add and link')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemoveProblem = async (problemId: string) => {
    try {
      await onRemoveProblem(problemId)
      toast.success('Problem removed')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to remove problem',
      )
    }
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose])

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      {/* Backdrop - closes modal on click */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="bg-card border-border relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b p-6">
          <div className="flex items-center gap-4">
            <AuthoredIcon
              icon={[studyPlan.icon, defaultIcon, studyPlan.source?.icon]}
              size="2xl"
              className="text-muted-foreground rounded"
              fallback={BookOpen}
              fallbackChip
            />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-foreground text-xl font-semibold">
                  {studyPlan.name}
                </h2>
                {(studyPlan.url || defaultUrl || studyPlan.source?.url) && (
                  <a
                    href={
                      (studyPlan.url ||
                        defaultUrl ||
                        studyPlan.source?.url) as string
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                    title="View original source"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
              {studyPlan.description && (
                <p className="text-muted-foreground text-sm">
                  {studyPlan.description}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Problems Section */}
          <div className="mb-6">
            {!showAddSection ? (
              <Button
                variant="outline"
                onClick={() => setShowAddSection(true)}
                className="w-full"
              >
                <Plus size={18} className="mr-2" />
                Add Problems
              </Button>
            ) : (
              <div className="border-border rounded-lg border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-foreground font-medium">Add Problems</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSection(false)
                      setSearchQuery('')
                      setSelectedProblemIds(new Set())
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="relative mb-3">
                  <Search
                    size={16}
                    className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                  />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search problems by name or ID..."
                    className="pl-9"
                  />
                </div>
                {questionsLoading ? (
                  <div className="text-muted-foreground flex items-center gap-2 py-4 text-center text-sm">
                    <RefreshCw size={14} className="animate-spin" />
                    Loading questions...
                  </div>
                ) : searchQuery.trim() ? (
                  filteredQuestions.length > 0 ? (
                    <div className="max-h-48 space-y-1 overflow-y-auto">
                      {filteredQuestions.map((q) => (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => handleToggleProblem(q.id)}
                          className={`hover:bg-muted/50 flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors ${
                            selectedProblemIds.has(q.id) ? 'bg-primary/10' : ''
                          }`}
                        >
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded border ${
                              selectedProblemIds.has(q.id)
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-muted-foreground'
                            }`}
                          >
                            {selectedProblemIds.has(q.id) && (
                              <Check size={12} />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {q.externalId && (
                                <span className="text-muted-foreground font-mono text-xs">
                                  #{q.externalId}
                                </span>
                              )}
                              <span className="text-foreground text-sm">
                                {q.name}
                              </span>
                            </div>
                          </div>
                          {q.difficulty && (
                            <span
                              className={`rounded px-1.5 py-0.5 text-xs font-medium ${getDifficultyColorClass(q.difficulty)}`}
                            >
                              {getDifficultyLabel(q.difficulty)}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      No matching problems found
                    </p>
                  )
                ) : (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    Start typing to search problems...
                  </p>
                )}
                {selectedProblemIds.size > 0 && (
                  <div className="mt-3 flex justify-end gap-2">
                    <Button
                      onClick={handleAddSelected}
                      disabled={isAdding}
                      size="sm"
                      variant={sourceId ? 'outline' : 'default'}
                    >
                      {isAdding
                        ? 'Adding...'
                        : `Add ${selectedProblemIds.size} Problem(s)`}
                    </Button>
                    {sourceId && onAddAndLinkSource && (
                      <Button
                        onClick={handleAddAndLinkSource}
                        disabled={isAdding}
                        size="sm"
                      >
                        {isAdding ? 'Adding...' : 'Add & Link Source'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Existing Problems */}
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-foreground text-lg font-medium">
              Problems ({studyPlan.problemCount})
            </h3>
          </div>

          {studyPlan.problems.length === 0 ? (
            <div className="py-12 text-center">
              <div className="bg-muted/50 mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
                <BookOpen size={32} className="text-muted-foreground" />
              </div>
              <h4 className="text-foreground mb-2 font-medium">
                No problems yet
              </h4>
              <p className="text-muted-foreground mx-auto max-w-sm text-sm">
                Add problems to this study plan using the button above.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {studyPlan.problems.map((problem) => (
                <div
                  key={problem.id}
                  className="bg-muted/30 hover:bg-muted/50 group flex items-center justify-between rounded-lg p-4 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {problem.externalId && (
                        <span className="text-muted-foreground font-mono text-sm">
                          #{problem.externalId}
                        </span>
                      )}
                      <h4 className="text-foreground font-medium">
                        {problem.title}
                      </h4>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      {problem.difficulty && (
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${getDifficultyColorClass(problem.difficulty)}`}
                        >
                          {getDifficultyLabel(problem.difficulty)}
                        </span>
                      )}
                      <span className="text-muted-foreground text-xs">
                        {problem.source}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveProblem(problem.id)}
                    className={cn(
                      REVEAL_ON_HOVER,
                      'text-muted-foreground hover:text-destructive ml-2 rounded p-2 transition-all',
                    )}
                    title="Remove from study plan"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// EXPORT STUDY PLANS MODAL
// =============================================================================

interface ExportStudyPlansModalProps {
  isOpen: boolean
  onClose: () => void
  studyPlans: StudyPlan[]
}

function formatDifficulty(
  difficulty: { name: string } | null | undefined,
): string {
  if (!difficulty) return ''
  return difficulty.name
}

function sanitizeSheetName(name: string): string {
  // Excel sheet names have restrictions:
  // - Max 31 characters
  // - Cannot contain: \ / * ? [ ] :
  let sanitized = name.replace(/[\\/*?[\]:]/g, '-')
  if (sanitized.length > 31) {
    sanitized = sanitized.slice(0, 31)
  }
  return sanitized
}

function generateExportXLSX(
  studyPlans: StudyPlan[],
  selectedPlanIds: Set<string>,
): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new()

  // Get selected plans
  const selectedPlans = studyPlans.filter((sp) => selectedPlanIds.has(sp.id))

  // ==========================================================================
  // MASTER LIST SHEET - All unique questions with comma-separated study plans
  // ==========================================================================
  const problemMap = new Map<
    string,
    {
      title: string
      url: string
      difficulty: string
      studyPlanNames: string[]
    }
  >()

  for (const plan of selectedPlans) {
    for (const problem of plan.problems) {
      const existing = problemMap.get(problem.id)
      if (existing) {
        // Problem already exists, add this study plan name if not already present
        if (!existing.studyPlanNames.includes(plan.name)) {
          existing.studyPlanNames.push(plan.name)
        }
      } else {
        // New problem
        problemMap.set(problem.id, {
          title: problem.title,
          url: problem.url || '',
          difficulty: formatDifficulty(problem.difficulty),
          studyPlanNames: [plan.name],
        })
      }
    }
  }

  // Build master list rows
  const masterRows: Array<{
    'Question Name': string
    URL: string
    Difficulty: string
    'Study Plans': string
  }> = []

  for (const [, data] of problemMap) {
    masterRows.push({
      'Question Name': data.title,
      URL: data.url,
      Difficulty: data.difficulty,
      'Study Plans': data.studyPlanNames.join(', '),
    })
  }

  // Sort by question name
  masterRows.sort((a, b) =>
    a['Question Name'].localeCompare(b['Question Name']),
  )

  // Create master worksheet
  const masterWorksheet = XLSX.utils.json_to_sheet(masterRows)
  masterWorksheet['!cols'] = [
    { wch: 40 }, // Question Name
    { wch: 50 }, // URL
    { wch: 12 }, // Difficulty
    { wch: 60 }, // Study Plans (wider for comma-separated list)
  ]

  // Add Master List as the first sheet
  XLSX.utils.book_append_sheet(workbook, masterWorksheet, 'Master List')

  // ==========================================================================
  // INDIVIDUAL STUDY PLAN SHEETS
  // ==========================================================================
  for (const plan of selectedPlans) {
    // Build rows for this plan's problems
    const rows: Array<{
      'Question Name': string
      URL: string
      Difficulty: string
      Topics: string
      'Study Plan': string
    }> = []

    for (const problem of plan.problems) {
      rows.push({
        'Question Name': problem.title,
        URL: problem.url || '',
        Difficulty: formatDifficulty(problem.difficulty),
        Topics: (problem.topics ?? []).map((t) => t.name).join(', '),
        'Study Plan': plan.name,
      })
    }

    // Sort rows by question name
    rows.sort((a, b) => a['Question Name'].localeCompare(b['Question Name']))

    // Create worksheet from data
    const worksheet = XLSX.utils.json_to_sheet(rows)

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 40 }, // Question Name
      { wch: 50 }, // URL
      { wch: 12 }, // Difficulty
      { wch: 50 }, // Topics
      { wch: 25 }, // Study Plan
    ]

    // Add worksheet to workbook with sanitized sheet name
    const sheetName = sanitizeSheetName(plan.name)
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  }

  return workbook
}

function downloadXLSX(workbook: XLSX.WorkBook): void {
  const timestamp = new Date().toISOString().split('T')[0]
  XLSX.writeFile(workbook, `study-plans-export-${timestamp}.xlsx`)
}

function ExportStudyPlansModal({
  isOpen,
  onClose,
  studyPlans,
}: ExportStudyPlansModalProps) {
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(
    () => new Set(),
  )

  // Filter to only plans with problems
  const eligiblePlans = useMemo(
    () => studyPlans.filter((sp) => sp.problemCount > 0),
    [studyPlans],
  )

  const handleTogglePlan = (planId: string) => {
    setSelectedPlanIds((prev) => {
      const next = new Set(prev)
      if (next.has(planId)) {
        next.delete(planId)
      } else {
        next.add(planId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedPlanIds.size === eligiblePlans.length) {
      // Deselect all
      setSelectedPlanIds(new Set())
    } else {
      // Select all
      setSelectedPlanIds(new Set(eligiblePlans.map((sp) => sp.id)))
    }
  }

  const handleExport = () => {
    if (selectedPlanIds.size === 0) return
    const workbook = generateExportXLSX(studyPlans, selectedPlanIds)
    downloadXLSX(workbook)
    onClose()
    toast.success('Excel file exported successfully')
  }

  const handleClose = () => {
    setSelectedPlanIds(new Set())
    onClose()
  }

  if (!isOpen) return null

  const isAllSelected =
    eligiblePlans.length > 0 && selectedPlanIds.size === eligiblePlans.length
  const canExport = selectedPlanIds.size > 0

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-card border-border flex max-h-[80vh] w-full max-w-md flex-col rounded-lg border shadow-lg">
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 p-2">
              <Download size={18} className="text-white" />
            </div>
            <h2 className="text-foreground text-lg font-semibold">
              Export Study Plans
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {eligiblePlans.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground text-sm">
                No study plans with questions available to export.
              </p>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground mb-4 text-sm">
                Select the study plans you want to include in the export. The
                CSV will contain all unique questions from the selected plans.
              </p>

              {/* Select All */}
              <button
                type="button"
                onClick={handleSelectAll}
                className="hover:bg-muted/50 mb-2 flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors"
              >
                <div
                  className={`flex h-5 w-5 items-center justify-center rounded border ${
                    isAllSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-muted-foreground'
                  }`}
                >
                  {isAllSelected && <Check size={12} />}
                </div>
                <span className="text-foreground text-sm font-medium">
                  Select All ({eligiblePlans.length})
                </span>
              </button>

              <div className="border-border my-2 border-t" />

              {/* Study Plan List */}
              <div className="space-y-1">
                {eligiblePlans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => handleTogglePlan(plan.id)}
                    className={`hover:bg-muted/50 flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors ${
                      selectedPlanIds.has(plan.id) ? 'bg-primary/10' : ''
                    }`}
                  >
                    <div
                      className={`flex h-5 w-5 items-center justify-center rounded border ${
                        selectedPlanIds.has(plan.id)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground'
                      }`}
                    >
                      {selectedPlanIds.has(plan.id) && <Check size={12} />}
                    </div>
                    <AuthoredIcon
                      icon={plan.icon}
                      className="text-muted-foreground rounded"
                      fallback={BookOpen}
                      fallbackChip
                    />
                    <div className="flex-1">
                      <span className="text-foreground text-sm">
                        {plan.name}
                      </span>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {plan.problemCount}{' '}
                      {plan.problemCount === 1 ? 'problem' : 'problems'}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-border flex justify-end gap-2 border-t p-4">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={!canExport}>
            <Download size={16} className="mr-1" />
            Export Excel
          </Button>
        </div>
      </div>
    </div>
  )
}

interface StudyPlansSectionProps {
  sourceId?: string
  defaultIcon?: string | null
  defaultUrl?: string | null
}

export function StudyPlansSection({
  sourceId,
  defaultIcon,
  defaultUrl,
}: StudyPlansSectionProps = {}) {
  const {
    studyPlans,
    loading,
    error,
    refresh,
    createStudyPlan,
    updateStudyPlan,
    deleteStudyPlan,
    addProblemsToStudyPlan,
    removeProblemsFromStudyPlan,
  } = useStudyPlans(sourceId, 'coding')

  const [isCollapsed, setIsCollapsed] = useState(!sourceId)
  const [showModal, setShowModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [editingPlan, setEditingPlan] = useState<StudyPlan | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<StudyPlan | null>(null)
  const [planToDelete, setPlanToDelete] = useState<StudyPlan | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleCreateOrUpdate = async (data: StudyPlanFormData) => {
    setIsSubmitting(true)
    try {
      if (editingPlan) {
        await updateStudyPlan(editingPlan.id, data)
        toast.success('Study plan updated')
      } else {
        await createStudyPlan(data)
        toast.success('Study plan created')
      }
      setShowModal(false)
      setEditingPlan(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = (e: React.MouseEvent, plan: StudyPlan) => {
    e.stopPropagation()
    setEditingPlan(plan)
    setShowModal(true)
  }

  const handleDelete = (e: React.MouseEvent, plan: StudyPlan) => {
    e.stopPropagation()
    setPlanToDelete(plan)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!planToDelete) return
    setIsSubmitting(true)
    try {
      await deleteStudyPlan(planToDelete.id)
      toast.success('Study plan deleted')
      setShowDeleteConfirm(false)
      setPlanToDelete(null)
      if (selectedPlan?.id === planToDelete.id) {
        setShowDetailModal(false)
        setSelectedPlan(null)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePlanClick = (plan: StudyPlan) => {
    setSelectedPlan(plan)
    setShowDetailModal(true)
  }

  const handleAddProblems = async (problemIds: string[]) => {
    if (!selectedPlan) return
    await addProblemsToStudyPlan(selectedPlan.id, problemIds)
    // Update local selected plan state
    const updated = studyPlans.find((sp) => sp.id === selectedPlan.id)
    if (updated) setSelectedPlan(updated)
  }

  const handleAddAndLinkSource = async (problemIds: string[]) => {
    if (!selectedPlan || !sourceId) return
    await addProblemsToStudyPlan(selectedPlan.id, problemIds, sourceId)
    // Update local selected plan state
    const updated = studyPlans.find((sp) => sp.id === selectedPlan.id)
    if (updated) setSelectedPlan(updated)
  }

  const handleRemoveProblem = async (problemId: string) => {
    if (!selectedPlan) return
    await removeProblemsFromStudyPlan(selectedPlan.id, [problemId])
    // Update local selected plan state
    const updated = studyPlans.find((sp) => sp.id === selectedPlan.id)
    if (updated) setSelectedPlan(updated)
  }

  // Group study plans by source
  const groupedPlans = useMemo(
    () => groupStudyPlansBySource(studyPlans),
    [studyPlans],
  )

  // Keep selected plan in sync with studyPlans
  const currentSelectedPlan = selectedPlan
    ? studyPlans.find((sp) => sp.id === selectedPlan.id) || selectedPlan
    : null

  if (loading) {
    return (
      <div className="bg-card border-border mt-8 rounded-lg border">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hover:bg-muted/50 flex w-full items-center justify-between p-4 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 p-2">
              <BookOpen size={20} className="text-white" />
            </div>
            <div className="text-left">
              <h2 className="text-foreground text-lg font-semibold">
                Study Plans
              </h2>
              <p className="text-muted-foreground text-sm">Loading...</p>
            </div>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        {!isCollapsed && (
          <div className="border-border border-t p-4">
            <div className="text-muted-foreground flex items-center gap-2">
              <RefreshCw size={16} className="animate-spin" />
              Loading study plans...
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-card border-border mt-8 rounded-lg border">
      {/* Collapsible Header */}
      <div className="flex items-center justify-between p-4">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hover:bg-muted/50 -m-2 flex flex-1 items-center gap-3 rounded-lg p-2 transition-colors"
        >
          <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 p-2">
            <BookOpen size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-foreground text-lg font-semibold">
              Study Plans
            </h2>
            <p className="text-muted-foreground text-sm">
              {studyPlans.length} study plan{studyPlans.length !== 1 ? 's' : ''}
            </p>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowModal(true)}>
            <Plus size={16} className="mr-1" />
            Add Study Plan
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowExportModal(true)}
          >
            <Download size={16} className="mr-1" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Content - Collapsible */}
      {!isCollapsed && (
        <div className="border-border border-t p-4">
          {error ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground mb-2">{error}</p>
              <Button variant="outline" size="sm" onClick={refresh}>
                Retry
              </Button>
            </div>
          ) : studyPlans.length === 0 ? (
            <div className="py-8 text-center">
              <div className="bg-muted mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full">
                <BookOpen size={24} className="text-muted-foreground" />
              </div>
              <h3 className="text-foreground mb-2 font-medium">
                No study plans yet
              </h3>
              <p className="text-muted-foreground mb-4 text-sm">
                Create your first study plan to organize problems.
              </p>
              <Button size="sm" onClick={() => setShowModal(true)}>
                <Plus size={16} className="mr-1" />
                Create Study Plan
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedPlans.map((group) => (
                <div key={group.sourceId}>
                  {/* Source header */}
                  <div className="mb-2 flex items-center gap-2 px-3">
                    <AuthoredIcon
                      icon={group.sourceIcon}
                      className="text-muted-foreground"
                      fallback={BookOpen}
                    />
                    <span className="text-muted-foreground text-sm font-medium">
                      {group.sourceName}
                    </span>
                    <span className="text-muted-foreground/60 text-xs">
                      ({group.plans.length})
                    </span>
                  </div>
                  {/* Plans under this source */}
                  <div className="space-y-1">
                    {group.plans.map((plan) => {
                      const planIcon = [
                        plan.icon,
                        defaultIcon,
                        group.sourceIcon,
                      ]
                      return (
                        <div
                          key={plan.id}
                          {...activatableRowProps({
                            onActivate: () => handlePlanClick(plan),
                            label: `Open ${plan.name}`,
                          })}
                          className="hover:bg-muted/50 group ml-7 flex cursor-pointer items-center gap-4 rounded-lg p-3 transition-colors"
                        >
                          <AuthoredIcon
                            icon={planIcon}
                            size="xl"
                            className="text-muted-foreground rounded"
                            fallback={BookOpen}
                            fallbackChip
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-foreground font-medium">
                                {plan.name}
                              </h3>
                              <span className="bg-primary/10 text-primary rounded px-2 py-0.5 text-xs font-medium">
                                {plan.problemCount}{' '}
                                {plan.problemCount === 1
                                  ? 'problem'
                                  : 'problems'}
                              </span>
                            </div>
                            {plan.description && (
                              <p className="text-muted-foreground line-clamp-1 text-sm">
                                {plan.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => handleEdit(e, plan)}
                              className={cn(
                                REVEAL_ON_HOVER,
                                'text-muted-foreground hover:text-foreground rounded p-2 transition-all',
                              )}
                              title="Edit study plan"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleDelete(e, plan)}
                              className={cn(
                                REVEAL_ON_HOVER,
                                'text-muted-foreground hover:text-destructive rounded p-2 transition-all',
                              )}
                              title="Delete study plan"
                            >
                              <Trash2 size={16} />
                            </button>
                            <ChevronRight
                              size={18}
                              className={cn(
                                REVEAL_ON_HOVER_DECORATIVE,
                                'text-muted-foreground',
                              )}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <StudyPlanFormModal
        isOpen={showModal}
        studyPlan={editingPlan}
        onClose={() => {
          setShowModal(false)
          setEditingPlan(null)
        }}
        onSubmit={handleCreateOrUpdate}
        isSubmitting={isSubmitting}
        defaultIcon={defaultIcon}
        defaultUrl={defaultUrl}
        namePlaceholder="e.g., Blind 75"
        descriptionPlaceholder="A curated list of problems..."
      />

      {/* Detail Modal */}
      {showDetailModal && currentSelectedPlan && (
        <StudyPlanDetailModal
          studyPlan={currentSelectedPlan}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedPlan(null)
          }}
          onAddProblems={handleAddProblems}
          onAddAndLinkSource={sourceId ? handleAddAndLinkSource : undefined}
          onRemoveProblem={handleRemoveProblem}
          sourceId={sourceId}
          defaultIcon={defaultIcon}
          defaultUrl={defaultUrl}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && planToDelete && (
        <div className="bg-background/80 fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border-border w-full max-w-md rounded-lg border p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-destructive/10 flex h-10 w-10 items-center justify-center rounded-full">
                <AlertTriangle size={20} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-foreground text-lg font-semibold">
                  Delete Study Plan
                </h2>
                <p className="text-muted-foreground text-sm">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-foreground mb-6">
              Are you sure you want to delete{' '}
              <strong>{planToDelete.name}</strong>? This will remove the study
              plan but won't affect the problems themselves.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setPlanToDelete(null)
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportStudyPlansModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        studyPlans={studyPlans}
      />
    </div>
  )
}
